import React, { useState, useEffect } from 'react'
import {X, Shield, AlertTriangle, CheckCircle, XCircle, Trash2, Eye} from 'lucide-react'
import { lumi } from '@/lib/lumi'

interface AdminPanelProps {
  isOpen: boolean
  onClose: () => void
}

export function AdminPanel({ isOpen, onClose }: AdminPanelProps) {
  const [adminKey, setAdminKey] = useState('')
  const [authenticated, setAuthenticated] = useState(false)
  const [reports, setReports] = useState<any[]>([])
  const [agents, setAgents] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedTab, setSelectedTab] = useState<'reports' | 'users'>('reports')

  if (!isOpen) return null

  const handleLogin = async () => {
    setLoading(true)
    try {
      const response = await fetch(`https://api.lumi.new/v1/functions/p411756259739451392/getReports?adminKey=${adminKey}`)
      if (response.ok) {
        const data = await response.json()
        setReports(data.reports || [])
        setAuthenticated(true)
        fetchAgents()
      } else {
        alert('Invalid admin key')
      }
    } catch (error) {
      console.error('Login error:', error)
      alert('Login failed')
    } finally {
      setLoading(false)
    }
  }

  const fetchAgents = async () => {
    try {
      const { list } = await lumi.entities.agents.list({
        filter: {},
        sort: { createdAt: -1 },
        limit: 100
      })
      setAgents(list)
    } catch (error) {
      console.error('Error fetching agents:', error)
    }
  }

  const updateReportStatus = async (reportId: string, status: string, priority?: string, adminNotes?: string) => {
    try {
      const response = await fetch('https://api.lumi.new/v1/functions/p411756259739451392/updateReportStatus', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminKey, reportId, status, priority, adminNotes })
      })
      
      if (response.ok) {
        // Refresh reports
        const reportsResponse = await fetch(`https://api.lumi.new/v1/functions/p411756259739451392/getReports?adminKey=${adminKey}`)
        const data = await reportsResponse.json()
        setReports(data.reports || [])
      }
    } catch (error) {
      console.error('Error updating report:', error)
    }
  }

  const deleteUser = async (agentId: string) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return
    }

    try {
      const response = await fetch('https://api.lumi.new/v1/functions/p411756259739451392/adminDeleteUser', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminKey, agentId })
      })
      
      if (response.ok) {
        alert('User deleted successfully')
        fetchAgents()
      }
    } catch (error) {
      console.error('Error deleting user:', error)
      alert('Failed to delete user')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-yellow-400 bg-yellow-400/10'
      case 'reviewing': return 'text-blue-400 bg-blue-400/10'
      case 'resolved': return 'text-green-400 bg-green-400/10'
      case 'dismissed': return 'text-gray-400 bg-gray-400/10'
      default: return 'text-gray-400 bg-gray-400/10'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-red-400 bg-red-400/10'
      case 'high': return 'text-orange-400 bg-orange-400/10'
      case 'medium': return 'text-yellow-400 bg-yellow-400/10'
      case 'low': return 'text-green-400 bg-green-400/10'
      default: return 'text-gray-400 bg-gray-400/10'
    }
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-black border border-deep-purple-800 rounded-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-black border-b border-deep-purple-800 p-4 sm:p-6 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-electric-purple-500" />
            <h2 className="text-xl sm:text-2xl font-bold text-white">Admin Panel</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {!authenticated ? (
            <div className="max-w-md mx-auto mt-12">
              <div className="text-center mb-6">
                <Shield className="w-16 h-16 text-electric-purple-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Admin Authentication</h3>
                <p className="text-gray-400">Enter admin key to access panel</p>
              </div>
              <div className="space-y-4">
                <input
                  type="password"
                  value={adminKey}
                  onChange={(e) => setAdminKey(e.target.value)}
                  placeholder="Admin Key"
                  className="w-full bg-deep-purple-950/30 border border-deep-purple-800 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                />
                <button
                  onClick={handleLogin}
                  disabled={loading || !adminKey}
                  className="w-full px-6 py-3 bg-gradient-to-r from-electric-purple-500 to-deep-purple-600 text-white rounded-lg hover:from-electric-purple-600 hover:to-deep-purple-700 transition-all disabled:opacity-50"
                >
                  {loading ? 'Authenticating...' : 'Login'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex gap-2 sm:gap-4 mb-6 border-b border-deep-purple-800 overflow-x-auto">
                <button
                  onClick={() => setSelectedTab('reports')}
                  className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
                    selectedTab === 'reports'
                      ? 'text-electric-purple-500 border-b-2 border-electric-purple-500'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Reports ({reports.length})
                </button>
                <button
                  onClick={() => setSelectedTab('users')}
                  className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
                    selectedTab === 'users'
                      ? 'text-electric-purple-500 border-b-2 border-electric-purple-500'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Users ({agents.length})
                </button>
              </div>

              {selectedTab === 'reports' ? (
                <div className="space-y-4">
                  {reports.length === 0 ? (
                    <p className="text-center text-gray-400 py-12">No reports yet</p>
                  ) : (
                    reports.map((report) => (
                      <div key={report._id} className="bg-deep-purple-950/20 border border-deep-purple-800 rounded-lg p-4 sm:p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                                {report.status}
                              </span>
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(report.priority)}`}>
                                {report.priority}
                              </span>
                              <span className="text-xs text-gray-500">
                                {report.reportType === 'bug' ? '🐛 Bug' : '👤 User'}
                              </span>
                            </div>
                            <h3 className="text-base sm:text-lg font-bold text-white mb-2 break-words">{report.subject}</h3>
                            <p className="text-gray-400 text-sm mb-4 break-words">{report.description}</p>
                            <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                              <span className="break-all">From: {report.reportedBy}</span>
                              {report.reporterEmail && <span className="break-all">Email: {report.reporterEmail}</span>}
                              {report.reportedUser && <span className="break-all">User: {report.reportedUser}</span>}
                              <span>{new Date(report.createdAt).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => updateReportStatus(report._id, 'reviewing')}
                            className="px-3 sm:px-4 py-2 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30 transition-colors text-xs sm:text-sm flex-shrink-0"
                          >
                            <Eye className="w-4 h-4 inline mr-1" />
                            Review
                          </button>
                          <button
                            onClick={() => updateReportStatus(report._id, 'resolved')}
                            className="px-3 sm:px-4 py-2 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 transition-colors text-xs sm:text-sm flex-shrink-0"
                          >
                            <CheckCircle className="w-4 h-4 inline mr-1" />
                            Resolve
                          </button>
                          <button
                            onClick={() => updateReportStatus(report._id, 'dismissed')}
                            className="px-3 sm:px-4 py-2 bg-gray-500/20 text-gray-400 rounded hover:bg-gray-500/30 transition-colors text-xs sm:text-sm flex-shrink-0"
                          >
                            <XCircle className="w-4 h-4 inline mr-1" />
                            Dismiss
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {agents.map((agent) => (
                    <div key={agent._id} className="bg-deep-purple-950/20 border border-deep-purple-800 rounded-lg p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                        {agent.profilePicture ? (
                          <img src={agent.profilePicture} alt={agent.name} className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-electric-purple-500 to-deep-purple-700 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">
                            {agent.name?.charAt(0) || '?'}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <h3 className="text-base sm:text-lg font-bold text-white truncate">{agent.name}</h3>
                          <p className="text-sm text-gray-400">
                            Rating: {agent.rating || 0} ⭐ | Tasks: {agent.tasksCompleted || 0}
                          </p>
                          <p className="text-xs text-gray-500">
                            Joined: {agent.createdAt ? new Date(agent.createdAt).toLocaleDateString() : 'Unknown'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => deleteUser(agent._id)}
                        className="px-3 sm:px-4 py-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-colors flex items-center gap-2 text-xs sm:text-sm flex-shrink-0 w-full sm:w-auto justify-center"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
